import _ from 'lodash';
import Async from 'tools/async';
import { cos } from 'helper/cos/index';
import { tips } from 'tools/utils';
import eventProxy from 'tools/eventProxy';

const { lang } = window;

// 删除控制参数
const ListMaxKeys = 1000; // 每次列出 1000 条
const DeleteBatchLimit = 100; // 每批删除 100 条
const DeleteParallelLimit = 10; // 单文件删除并发 10 条
const DeleteInterval = 800; // 删除频控间隔

// 删除全局计数器
let globalSuccessCount = 0;
let globalErrorCount = 0;
let failedList = [];
let successList = [];
let totalList = [];
// let runningTask = 0;

// 对一个列表，相同 Key 拆散在不同分组里
const taskScatter = list => {
  const indexMap = {};
  const groups = [];
  list.forEach(item => {
    const Key = item.key || item.Key;
    const index = indexMap[Key] || 0;
    if (!groups[index]) {
      groups[index] = [];
    }
    groups[index].push(item);
    indexMap[Key] = index + 1;
  });
  return groups;
};

// 批量删除文件
const deleteFiles = (params, objectList, callback) => {
  let successCount = 0;
  let errorCount = 0;
  const formatObjects = list => list.map(item => {
    const obj = { Key: item.Key };
    if (item.VersionId !== undefined) {
      obj.VersionId = item.VersionId;
    }
    return obj;
  });

  const multiDelete = nextDeleter => {
    cos.deleteMultipleObject(
      {
        Bucket: params.Bucket,
        Region: params.Region,
        Objects: formatObjects(objectList),
      },
      (err, data) => {
        if (err) {
          if (err.statusCode === 403 && err.error.code === 'AccessDenied') {
            callback('no_permissions_to_delete_object'); // 无权限，结束删除
          } else if (err.error.code === 'ECONNRESET') {
            callback('ECONNRESET');
          } else if (nextDeleter) {
            nextDeleter();
          } else {
            callback(err.error);
          }
        } else {
          successList = data.Deleted;
          successCount += data.Deleted.length;
          if (data.Error.length) {
            objectList = data.Error;
            if (nextDeleter) {
              // 下一个删除方法
              nextDeleter();
              return;
            }
            // 若没有下一步的删除操作，则保存删除失败的文件
            failedList = objectList;
            errorCount = objectList.length;
          }
          callback(null, { successCount, errorCount }); // 全部完成，结束删除
        }
      },
    );
  };

  const singleDelete = nextDeleter => {
    const unFinishList = [];
    let errStop = false;
    let doing = 0;
    Async.eachLimit(
      objectList,
      DeleteParallelLimit,
      (item, next) => {
        // 遇到有错误，则不再开始新的并发任务
        if (errStop) return;

        doing++;
        const { Key, VersionId } = item;
        cos.deleteObject(
          {
            Bucket: params.Bucket,
            Region: params.Region,
            Key,
            VersionId,
          },
          err => {
            doing--;
            if (err) {
              errStop = true;

              const { error } = err;
              unFinishList.push(item);

              if (!nextDeleter) {
                failedList.push({ Key, VersionId, Code: error.Code });
              }

              if (errStop) {
                if (doing === 0 && !nextDeleter) {
                  errorCount = unFinishList.length;
                  callback(null, { successCount, errorCount }); // 全部完成，结束删除
                } else {
                  next();
                }
              } else {
                next();
              }
            } else {
              successList.push(item);
              successCount++;
              next();
            }
          },
        );
      },
      () => {
        if (errStop && !nextDeleter) return;
        if (unFinishList.length && nextDeleter) {
          nextDeleter(); // 下一个删除方法
        } else {
          callback(null, { successCount, errorCount }); // 全部完成，结束删除
        }
      },
    );
  };

  if (objectList.length === 1) {
    singleDelete(multiDelete);
  } else {
    multiDelete(singleDelete);
  }
};

// 控制每 800ms 只有 100 个文件删除
const limitDeleteFiles = (function limitDeleteFilesFn() {
  let deleteAllowTime = Date.now();
  const deleteQueue = [];
  const run = cb => {
    const task = deleteQueue.shift();
    if (!task || !task.objectList) return;
    deleteAllowTime = Date.now() + Math.round((DeleteInterval / 100) * task.objectList.length);
    deleteFiles(task.params, task.objectList, (err, data) => {
      task.callback(err, data);
      cb();
    });
  };
  const check = () => {
    const time1 = Date.now();
    if (time1 >= deleteAllowTime) {
      run(check);
    } else {
      setTimeout(check, deleteAllowTime - time1);
    }
  };

  return (params, objectList, callback) => {
    deleteQueue.push({ params, objectList, callback });
    check();
  };
}());

// 把批量文件拆成分组来删除
const startDeleteFiles = (params, objectList, callback) => {
  const result = { successCount: 0, errorCount: 0 };
  const taskGroups = taskScatter(objectList);
  Async.eachLimit(
    taskGroups,
    1,
    (taskGroup, next) => {
      limitDeleteFiles(params, taskGroup, (err, data) => {
        if (err) {
          callback(err, data);
          return;
        }
        if (data) {
          result.successCount += data.successCount;
          result.errorCount += data.errorCount;
        }
        next();
      });
    },
    () => {
      callback(null, result);
    },
  );
};

const fileDeleter = (params, someFinish, finishCallback) => {
  // runningTask++;
  const _finishCallback = (err, result) => {
    finishCallback(err, result);
    // runningTask--;
    // if (!runningTask) {
    globalSuccessCount = 0;
    globalErrorCount = 0;
    failedList = [];
    successList = [];
    totalList = [];
    // }
  };

  if (!params.List.length) {
    _finishCallback({ code: 'no_file_to_delete' });
    return;
  }

  const { Versioning } = params;
  const { VersioningType } = params;

  let queueWaiting = [];
  let queueRunning = [];

  let totalCount = 0;
  let successCount = 0;

  let scanEnded = false;

  // 每次执行消化掉 DeleteBatchLimit 个
  const digestSome = digestFinish => {
    // 已经扫描结束 || 等待中的大于 100
    if (queueWaiting.length && (scanEnded || queueWaiting.length >= DeleteBatchLimit)) {
      // 从删除等待文件列表取出尽可能 Key 不一致的 100 个文件
      queueWaiting = _.flatten(taskScatter(queueWaiting));
      // 取出 100 个文件放入执行删除队列
      queueRunning = queueWaiting.splice(0, DeleteBatchLimit);

      totalCount += queueRunning.length;
      totalList.push(...queueRunning);

      const delParams = {
        Bucket: params.Bucket,
        Region: params.Region,
      };
      // 开始删除文件
      startDeleteFiles(delParams, queueRunning, (err, result) => {
        if (err) {
          _finishCallback(err);
          return;
        }

        successCount += result.successCount;
        globalSuccessCount += result.successCount;
        globalErrorCount += result.errorCount;
        result.globalSuccessCount = globalSuccessCount;
        result.globalErrorCount = globalErrorCount;
        if (someFinish) {
          someFinish(null, result);
        }
        digestSome(digestFinish);
      });
    } else {
      digestFinish();
    }
  };

  // 扫描单个目录
  const scanDir = (dirKey, scanDirFinish) => {
    let Marker;
    let KeyMarker;
    let VersionIdMarker;
    const next = () => {
      cos[Versioning ? 'listObjectVersions' : 'getBucket'](
        {
          Bucket: params.Bucket,
          Region: params.Region,
          Prefix: dirKey,
          Marker,
          KeyMarker,
          VersionIdMarker,
          MaxKeys: ListMaxKeys,
        },
        (err, data) => {
          if (err) {
            const msg = Versioning ? 'no_permissions_to_list_object_versions' : 'no_permissions_to_get_bucket';
            _finishCallback(err.statusCode === 403 ? msg : err.error);
          } else if (data) {
            let list = [];
            if (data.Contents && data.Contents.length) list.push(...data.Contents);
            if (data.Versions && data.Versions.length) list.push(...data.Versions);
            if (data.DeleteMarkers && data.DeleteMarkers.length) list.push(...data.DeleteMarkers);
            if (VersioningType === 'history') {
              list = list.filter(item => item.IsLatest === 'false'); // 删除当前文件的历史版本
              list.push(...data.DeleteMarkers.filter(item => item.IsLatest === 'true')); // 删除当前文件为删除标记的版本
            }
            queueWaiting.push(...list);

            if (data.IsTruncated === 'true') {
              Marker = data.NextMarker;
              KeyMarker = data.NextKeyMarker;
              VersionIdMarker = data.NextVersionIdMarker;
              digestSome(next);
            } else {
              digestSome(scanDirFinish);
            }
          }
        },
      );
    };
    next();
  };

  // 遍历任务
  const scanTaskList = scanTasksFinish => {
    const listDir = [];

    params.List.forEach(item => {
      if (
        (item.Key === '' || item.Key.substr(item.Key.length - 1) === '/')
        && item.isDir
        && (item.VersionId === undefined || item.VersionId === '')
      ) {
        listDir.push(item.Key);
      } else if (
        VersioningType !== 'history' // 删除操作非删除历史版本
        || (VersioningType === 'history' && item.IsLatest === 'false') // 删除历史对象版本
      ) {
        queueWaiting.push(item);
      }
    });

    digestSome(() => {
      Async.eachLimit(listDir, 1, scanDir, () => {
        scanEnded = true;
        digestSome(scanTasksFinish);
      });
    });
  };

  // 遍历每个任务
  scanTaskList(() => {
    _finishCallback(null, {
      globalSuccessCount,
      globalErrorCount,
      successCount,
      // errorCount,
      totalCount,
      failedList,
      successList,
      totalList,
    });
  });
};

export default (params, someFinish, finishCallback) => {
  const _params = _.cloneDeep(params);
  _params.List = _params.List.map(item => {
    if (item.key === '' || item.key) {
      item.Key = item.key;
      delete item.key;
    }
    if (item.versionId === '' || item.versionId) {
      item.VersionId = item.versionId;
      delete item.versionId;
    }
    return item;
  });

  fileDeleter(_params, someFinish, (err, data) => {
    tips.closeTips();
    if (err) {
      const code = err.Code || err.code;
      if (!code) {
        tips.error(lang.t(`error.${err}`));
      } else {
        tips.error(lang.err(`error['${code}']`, err));
      }
      finishCallback(err);
      return;
    }

    const {
      globalSuccessCount: successCount,
      globalErrorCount: errorCount,
      totalCount,
      failedList: _failedList,
      successList: _successList,
      totalList: _totalList,
    } = data;
    if (totalCount > 0) {
      if (!_failedList.length && successCount) {
        tips.success(lang.t('core.delete_files_success', { count: totalCount }));
      } else {
        const closeNotification = tips.notification({
          type: 'error',
          content: h => {
            const unfinishedCount = totalCount - successCount - errorCount;
            return h('div', [
              h('p', lang.t('core.delete_files_paragraph[0]', { count: totalCount })),
              h('p', lang.t('core.delete_files_paragraph[1]', { count: successCount })),
              h('p', lang.t('core.delete_files_paragraph[2]', { count: errorCount })),
              h('p', lang.t('core.delete_files_paragraph[3]', { count: unfinishedCount })),
              h(
                'a',
                {
                  style: { cursor: 'pointer' },
                  on: {
                    click: () => {
                      closeNotification();
                      eventProxy.emit('deleteFailed.show', {
                        failedList: _failedList,
                        successList: _successList,
                        totalList: _totalList,
                        type: 'delete',
                      });
                    },
                  },
                },
                lang.t('core.view'),
              ),
            ]);
          },
        });
        eventProxy.once('after-logout', () => {
          if (closeNotification) closeNotification();
        });
      }
    } else {
      tips.error(lang.t('error.no_versioning_to_clear'));
    }

    finishCallback(err, data);
  });
};
